import { AvailableLocale, Nullish } from '@omnea-digital/toolbox'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import { z } from 'zod'
import { getCountryDisplayName } from '@omnea-app/packages/universal/countries/getCountryDisplayName'
import { isNotWhitespace } from '@omnea-app/packages/universal/string-utils/whitespace'

const AddressWithoutCountrySchema = z.object({
  city: z.string(),
  state: z.string().nullish(),
  street1: z.string(),
  street2: z.string().nullish(),
  zipCode: z.string(),
})

export const AddressSchema = z.object({ ...AddressWithoutCountrySchema.shape, country: z.string().optional() })

export const AddressWithCountrySchema = z.object({ ...AddressSchema.shape, country: z.string() })

export type Address = z.infer<typeof AddressSchema>
export type AddressWithCountry = z.infer<typeof AddressWithCountrySchema>
export type AddressWithoutCountry = z.infer<typeof AddressWithoutCountrySchema>
const AddressWithCountryRowKeyEnum = AddressWithCountrySchema.keyof()
export type AddressWithCountryRowKey = z.infer<typeof AddressWithCountryRowKeyEnum>
export const addressWithCountryRowKeys = AddressWithCountryRowKeyEnum.options as readonly AddressWithCountryRowKey[]

export const isAddress = (address: unknown): address is Address => {
  return AddressSchema.safeParse(address).success
}

export const isAddressWithCountry = (address: unknown): address is AddressWithCountry => {
  return AddressWithCountrySchema.safeParse(address).success
}

export const isAddressWithoutCountry = (address: unknown): address is AddressWithoutCountry => {
  return AddressWithoutCountrySchema.safeParse(address).success
}

export const isAddressValid = (address: Address, isCountryOptional?: boolean): boolean => {
  if (isCountryOptional) {
    return isAddressWithoutCountry(address) && !isEmpty(address.city) && !isEmpty(address.street1) && !isEmpty(address.zipCode)
  }

  return (
    isAddressWithCountry(address) &&
    !isEmpty(address.city) &&
    !isEmpty(address.country) &&
    !isEmpty(address.street1) &&
    !isEmpty(address.zipCode)
  )
}

// In some cases integrations will return incomplete addresses, so we need to be able to handle them
export type PartialAddress = {
  [K in keyof Address]?: Nullish<Address[K]>
}

export const isPartialAddress = (address: unknown): address is PartialAddress => {
  return Boolean(
    address &&
    typeof address === 'object' &&
    ('street1' in address ||
      'street2' in address ||
      'city' in address ||
      'zipCode' in address ||
      'country' in address ||
      'state' in address)
  )
}

export const areAddressFieldsEqual = (addressField1: Nullish<string>, addressField2: Nullish<string>): boolean => {
  // "" is considered the same as null
  return (addressField1 || null) === (addressField2 || null)
}

export const isPartialAddressEqual = (address1: PartialAddress, address2: PartialAddress): boolean => {
  const addressFields = ['street1', 'street2', 'city', 'zipCode', 'country'] as const
  for (const field of addressFields) {
    if (!areAddressFieldsEqual(address1[field], address2[field])) {
      return false
    }
  }
  return true
}

export const getAddressDisplayName = (address: {
  name: string | null
  addressLine1: string | null
  city?: string | null
  formattedAddress?: string | null
}): string => {
  return address.name ?? address.addressLine1 ?? address.city ?? address.formattedAddress ?? ''
}

/**
 * Attempts to parse raw data as an Address. If it doesn't match the schema exactly
 * but looks like a partial address, coerces missing fields to empty strings.
 * Returns null if the data is not recognisable as an address at all.
 */
export const coerceToAddress = (raw: unknown): { address: Address | null; coerced: boolean; errors?: z.ZodIssue[] } => {
  if (raw == null) return { address: null, coerced: false }

  const result = AddressSchema.safeParse(raw)
  if (result.success) return { address: result.data, coerced: false }

  if (!isPartialAddress(raw)) return { address: null, coerced: false, errors: result.error.issues }

  const stringOrEmpty = (value: unknown): string => (typeof value === 'string' ? value : '')
  const stringOrNullish = (value: unknown): string | null | undefined =>
    typeof value === 'string' ? value : value == null ? value : ''

  return {
    address: {
      city: stringOrEmpty(raw.city),
      street1: stringOrEmpty(raw.street1),
      zipCode: stringOrEmpty(raw.zipCode),
      street2: stringOrNullish(raw.street2),
      state: stringOrNullish(raw.state),
      country: typeof raw.country === 'string' ? raw.country : undefined,
    },
    coerced: true,
    errors: result.error.issues,
  }
}

export const addressToString = (locale: AvailableLocale, address: PartialAddress): string => {
  return [
    address.street1,
    address.street2,
    address.city,
    address.state,
    getCountryDisplayName(locale, address.country),
    address.zipCode,
  ]
    .filter(isString)
    .map((part) => part.trim())
    .filter(isNotWhitespace)
    .join(', ')
}
